// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import { Check, ChevronDown, Building2, FileText, Layers3, ScanLine } from 'lucide-react';
import { 
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import type { OrganizationResponse } from '../types/workspace';

interface OrganizationDropdownProps {
  accessibleOrganizations: OrganizationResponse[];
  selectedOrganizationId?: string;
  selectOrganization: (orgId: string) => void;
  disabled?: boolean;
}

export const OrganizationDropdown: React.FC<OrganizationDropdownProps> = ({
  accessibleOrganizations,
  selectedOrganizationId,
  selectOrganization,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  // Find currently selected organization
  const selectedOrg = accessibleOrganizations.find(
    (org) => org.orgId === selectedOrganizationId
  );

  if (accessibleOrganizations.length === 0) {
    return (
      <Button
        variant="outline"
        className="w-[240px] justify-between"
        disabled={true}
      >
        <span className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">No organizations available</span>
        </span>
      </Button>
    );
  }

  // If only one organization and it's already selected, just show it
  if (accessibleOrganizations.length === 1 && selectedOrg) {
    return (
      <Button
        variant="outline"
        className="w-[240px] justify-between"
        disabled={true}
      >
        <span className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{selectedOrg.name}</span>
        </span>
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-11 min-w-[240px] justify-between gap-2.5 rounded-[10px] px-3.5 text-[15px] transition-all duration-200',
            open && 'border-slate-400 bg-slate-50 shadow-sm',
          )}
          disabled={disabled}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{selectedOrg ? selectedOrg.name : 'Select Organization'}</span>
          </span>
          <ChevronDown className={cn(
            'h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200',
            open && 'rotate-180',
          )} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[300px] rounded-xl p-1.5 shadow-[0_16px_40px_rgba(15,23,42,0.16),0_3px_8px_rgba(15,23,42,0.06)]"
      >
        <DropdownMenuLabel className="px-2.5 pb-[5px] pt-[7px] text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400">
          Switch application
        </DropdownMenuLabel>
        {accessibleOrganizations.map((org, index) => {
          const presentation = getOrganizationPresentation(org)
          const Icon = presentation.Icon
          return (
            <React.Fragment key={org.orgId}>
              {presentation.organization && index > 0 && <DropdownMenuSeparator className="mx-1 my-[5px] bg-[#eef2f6]" />}
              <DropdownMenuItem
                onClick={() => selectOrganization(org.orgId)}
                className={cn(
                  'cursor-pointer gap-[11px] rounded-lg px-2.5 py-[9px] text-[15px] font-normal text-slate-900',
                  org.orgId === selectedOrganizationId && 'bg-blue-50 font-semibold focus:bg-blue-50 data-[highlighted]:bg-blue-50',
                )}
              >
                <span className={cn(
                  'flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500',
                  org.orgId === selectedOrganizationId && 'bg-blue-600 text-white',
                )}>
                  <Icon className={cn('h-4 w-4', org.orgId === selectedOrganizationId && 'text-white')} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate leading-[1.3]">{org.name}</span>
                  <span className={cn(
                    'block text-xs font-normal leading-[1.3]',
                    org.orgId === selectedOrganizationId ? 'text-slate-500' : 'text-slate-400',
                  )}>{presentation.hint}</span>
                </span>
                {org.orgId === selectedOrganizationId && <Check className="h-4 w-4 text-blue-600" strokeWidth={2.5} />}
              </DropdownMenuItem>
            </React.Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function getOrganizationPresentation(org: OrganizationResponse) {
  const value = `${org.orgId} ${org.name}`.toLowerCase()
  if (value.includes('base')) return { Icon: Layers3, hint: 'System defaults', organization: false }
  if (value.includes('template')) return { Icon: ScanLine, hint: 'Reusable starting points', organization: false }
  return { Icon: Building2, hint: 'Your organization', organization: true }
}
